/** Central GA4 boundary. Never pass user-entered strings, identity, or raw URLs. */
export type AnalyticsEvent =
  | "form_start" | "form_submit_attempt" | "form_error" | "generate_lead"
  | "contact_click" | "navigation_click" | "outbound_click" | "file_download"
  | "scroll_depth" | "section_view" | "view_item" | "select_item"
  | "catalog_filter" | "catalog_search" | "quote_open" | "quote_intent"
  | "login" | "sign_up" | "onboarding_complete" | "rfq_quote_sent" | "rfq_quote_revised";
type Params = Record<string, string | number | boolean | undefined>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    raiAnalytics?: { measurementId: string; adsConversion?: string; debug: boolean; lastPath?: string; lastLocation?: string };
  }
}

const routes = new Set([
  "/", "/about", "/areas", "/b2b", "/book", "/business", "/end-of-life-support", "/institutions", "/institutions/one-time",
  "/laptop-screen-repair", "/marketplace", "/partners", "/partners/login",
  "/partners/onboarding", "/partners/dashboard", "/partners/dashboard/profile",
  "/partners/dashboard/service-areas", "/account", "/account/login", "/account/setup",
  "/account/assets", "/account/assets/new", "/account/quotes", "/account/quotes/new",
  "/account/calls", "/account/team", "/account/profile", "/account/orders", "/account/support", "/account/notifications", "/privacy", "/quote", "/sourcing", "/ssd-upgrade", "/status", "/terms", "/vcard",
]);
export function safePath(raw: string): string {
  const path = raw.split(/[?#]/)[0].replace(/\/$/, "") || "/";
  if (routes.has(path)) return path;
  for (const prefix of ["m", "areas", "solutions"]) {
    if (path.startsWith(`/${prefix}/`)) return `/${prefix}/[slug]`;
  }
  return "/other";
}
const keys = new Set([
  "form_id", "field_name", "error_type", "contact_method", "placement", "destination",
  "percent_scrolled", "section_id", "item_id", "item_category", "tier", "category",
  "result_count", "has_results", "intent", "method", "file_extension",
]);
const safeToken = (value: string) => /^[a-zA-Z0-9_ ./[\]-]{1,80}$/.test(value) && !/\d{7,}/.test(value);
function send(...args: unknown[]) {
  try {
    if (window.raiAnalytics?.debug) console.debug("[Rai Analytics]", JSON.stringify(args));
    window.gtag?.(...args);
  } catch { /* Measurement must never break the customer journey. */ }
}

/**
 * `adsConversion` is a Google Ads conversion target (`AW-…/label`). It shares this gtag.js and its
 * opt-outs, and only the storefront passes it; the conversion fires with `generate_lead`.
 */
export function initializeAnalytics(measurementId: string, debug = false, adsConversion?: string) {
  if (typeof window === "undefined" || !/^G-[A-Z0-9]+$/.test(measurementId) || window.raiAnalytics) return;
  const ads = adsConversion && /^AW-\d+\/[\w-]+$/.test(adsConversion) ? adsConversion : undefined;
  window.raiAnalytics = { measurementId, adsConversion: ads, debug };
  window.dataLayer = window.dataLayer || [];
  // Google expects an Arguments object, not an array, in the dataLayer queue.
  // eslint-disable-next-line prefer-rest-params -- Required gtag queue contract.
  window.gtag = function () { window.dataLayer!.push(arguments); };
  send("js", new Date());
  send("set", {
    page_location: window.location.origin + safePath(window.location.pathname),
    page_referrer: "",
    page_title: safePath(window.location.pathname),
  });
  const campaign: Record<string, string> = {};
  const query = new URLSearchParams(window.location.search);
  for (const [key, parameter] of Object.entries({ utm_source: "campaign_source", utm_medium: "campaign_medium", utm_campaign: "campaign_name", utm_content: "campaign_content" })) {
    const value = query.get(key);
    if (value && safeToken(value)) campaign[parameter] = value;
  }
  send("config", measurementId, {
    send_page_view: false,
    ...campaign,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    ...(debug ? { debug_mode: true } : {}),
  });
  if (ads) send("config", ads.split("/")[0], { send_page_view: false, allow_ad_personalization_signals: false });
}

export function trackEvent(event: AnalyticsEvent, params: Params = {}) {
  if (typeof window === "undefined" || !window.raiAnalytics) return;
  const clean: Params = {};
  for (const [key, value] of Object.entries(params)) {
    if (!keys.has(key)) continue;
    if (typeof value === "string" && safeToken(value)) clean[key] = value;
    if (typeof value === "boolean" || (typeof value === "number" && Number.isFinite(value))) clean[key] = value;
  }
  const items = (event === "view_item" || event === "select_item") && clean.item_id
    ? [{ item_id: clean.item_id, ...(clean.item_category ? { item_category: clean.item_category } : {}) }]
    : undefined;
  const { measurementId, adsConversion, debug } = window.raiAnalytics;
  // Pinned to GA4 so the Ads account receives only its conversion, not every interaction.
  send("event", event, { ...clean, ...(items ? { items } : {}), page_path: safePath(window.location.pathname), send_to: measurementId, ...(debug ? { debug_mode: true } : {}) });
  // A saved enquiry is the Ads conversion: no value or transaction id, since nothing is sold here.
  if (event === "generate_lead" && adsConversion) send("event", "conversion", { send_to: adsConversion });
}

export function trackPageView(pathname: string, referrer: string) {
  if (typeof window === "undefined" || !window.raiAnalytics) return;
  const state = window.raiAnalytics;
  // Compare actual paths so different product details each get one view; send only normalized paths.
  if (state.lastPath === pathname) return;
  const path = safePath(pathname);
  const location = window.location.origin + path;
  let previous = state.lastLocation || "";
  if (!state.lastPath && referrer) {
    try { previous = new URL(referrer).origin; } catch { /* Ignore malformed referrers. */ }
  }
  const page = { page_location: location, page_path: path, page_title: path === "/" ? "Home" : path, page_referrer: previous };
  send("set", page);
  send("event", "page_view", { ...page, ...(state.debug ? { debug_mode: true } : {}) });
  state.lastPath = pathname;
  state.lastLocation = location;
}

export function classifyLink(href: string): { event: AnalyticsEvent; params: Params } | null {
  if (href.startsWith("tel:")) return { event: "contact_click", params: { contact_method: "phone" } };
  if (href.startsWith("mailto:")) return { event: "contact_click", params: { contact_method: "email" } };
  try {
    const url = new URL(href, window.location.origin);
    if (!["https:", "http:"].includes(url.protocol)) return null;
    if (["wa.me", "api.whatsapp.com", "web.whatsapp.com"].includes(url.hostname)) {
      return { event: "contact_click", params: { contact_method: "whatsapp" } };
    }
    const extension = url.pathname.match(/\.(pdf|zip|docx?|xlsx?|csv|pptx?|vcf)$/i)?.[1];
    if (extension) return { event: "file_download", params: { file_extension: extension.toLowerCase() } };
    if (url.origin !== window.location.origin) return { event: "outbound_click", params: { destination: url.hostname } };
    return { event: "navigation_click", params: { destination: safePath(url.pathname) } };
  } catch { return null; }
}
